import { useEffect, useState } from "react";
import { Bell, X, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { currentPushSubscription, enablePush, isIos, isPushSupported, isStandalone } from "@/lib/pwa";
import { api } from "@/lib/api";

const STORAGE_KEY = "stupeco.notif-primer.dismissed";

/**
 * NotificationPrimer — Command Center-level nudge that appears ONLY when the user has:
 *  1. finished onboarding (Command Center visible),
 *  2. saved at least one reminder-bearing item (task.reminder_at, deal.next_follow_up, event),
 *  3. not yet subscribed a push device AND not dismissed the primer.
 * We never auto-prompt Notification.requestPermission — the choice is user-initiated.
 */
export default function NotificationPrimer() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (typeof window === "undefined") return;
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
      if (!isPushSupported()) return;
      const sub = await currentPushSubscription().catch(() => null);
      if (sub) return; // Already subscribed on this device.
      try {
        const { data } = await api.get("/command-center/summary");
        const hasReminder =
          (data.reminders && data.reminders.length > 0) ||
          (data.deals_needing_followup && data.deals_needing_followup.length > 0) ||
          (data.upcoming_events && data.upcoming_events.length > 0);
        if (active && hasReminder) setShow(true);
      } catch { /* noop */ }
    })();
    return () => { active = false; };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled.");
      dismiss();
    } catch (err) {
      toast.error(err.message || "Could not enable push.");
    } finally {
      setBusy(false);
    }
  };

  const emailOnly = async () => {
    try {
      await api.patch("/notification-preferences", { push_enabled: false, email_enabled: true });
      toast.success("We'll email your reminders.");
      dismiss();
    } catch { toast.error("Could not save preference."); }
  };

  return (
    <div className="rounded-3xl border border-brand-teal/30 bg-brand-teal/5 p-5" data-testid="notif-primer">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal">
          <Bell className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-semibold tracking-tight">Never miss a reminder or follow-up.</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Pick how Workspace should reach you when a task, follow-up or meeting is due. You can change this anytime in Settings → Notifications.</p>
          {isIos() && !isStandalone() && (
            <p className="mt-2 text-[11px] text-muted-foreground">On iPhone/iPad, first Add Workspace to your Home Screen to enable push. Email reminders work either way.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={enable} disabled={busy || (isIos() && !isStandalone())} data-testid="notif-primer-push" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60"><Smartphone className="h-3.5 w-3.5" /> Enable Push</button>
            <button onClick={emailOnly} data-testid="notif-primer-email" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium"><Mail className="h-3.5 w-3.5" /> Email me instead</button>
            <Link to="/app/settings/notifications" data-testid="notif-primer-both" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium">Both / customise</Link>
            <button onClick={dismiss} data-testid="notif-primer-later" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">Maybe later</button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" data-testid="notif-primer-close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
